import styled, { css } from 'styled-components';


/**
 * Reusable css that adds the followign properties to a styled component:
 * $m (number (in pixels) or string) : margin
 * $mx (number (in pixels) or string) : margin-left and margin-right
 * $my (number (in pixels) or string) : margin-top and margin-bottom
 * $mt (number (in pixels) or string) : margin-top
 * $mb (number (in pixels) or string) : margin-bottom
 * $ml (number (in pixels) or string) : margin-left
 * $mr (number (in pixels) or string) : margin-right
 * $p (number (in pixels) or string) : padding
 * $px (number (in pixels) or string) : padding-left and padding-right
 * $py (number (in pixels) or string) : padding-top and padding-bottom
 * $pt (number (in pixels) or string) : padding-top
 * $pb (number (in pixels) or string) : padding-bottom
 * $pl (number (in pixels) or string) : padding-left
 * $pr (number (in pixels) or string) : padding-right
 * $d (string) : display (css property)
 * $f (string or number) : flex (for example flex: 1)
 * $fd (string) : flex-direction (for example flex-direction: column)
 * $column (string) : grid-column
 * $row (string) : grid-row
 * DEPRECATED - $mw is deprecated in favour of maxw
 * $mw (number (in pixels) or string) : max-width
 * $maxw (number (in pixels) or string) : max-width
 * $minw (number (in pixels) or string) : min-width
 * $w (number (in pixels) or string) : width
 * $bpw number (in pixels) or string) : @media { width } width when browser window width below breakpoint
 * $bp number (in pixels) or string) : @media(max-width) width for breakpoint (default is 1560px)
 * $minh (number (in pixels) or string) : min-height
 * $h (number (in pixels) or string) : height
 * DEPRECATED - $align is deprecated in favour of $alignSelf
 * $align (string) : align-self (for example align-self: center)
 * $alignSelf (string) : align-self (for example align-self: center)
 * $justify (string) : justify-self (for example justify-self: center)
 * $textAlign (string) : text-align (for example text-align: center)
 * $overflow (string) : overflow (for example overflow: hidden)
 */
export const Layout = css`
    margin: ${props => pxValue(props.$m)};
    margin-left: ${props => pxValue(props.$ml ?? props.$mx)};
    margin-right: ${props => pxValue(props.$mr ?? props.$mx)};
    margin-top: ${props => pxValue(props.$mt ?? props.$my)};
    margin-bottom: ${props => pxValue(props.$mb ?? props.$my)};

    padding: ${props => pxValue(props.$p)};
    padding-left: ${props => pxValue(props.$pl ?? props.$px)};
    padding-right: ${props => pxValue(props.$pr ?? props.$px)};
    padding-top: ${props => pxValue(props.$pt ?? props.$py)};
    padding-bottom: ${props => pxValue(props.$pb ?? props.$py)};

    display: ${props => props.$hidden ? 'none' : props.$d};
    flex: ${props => props.$f};
    grid-column: ${props => props.$column};
    grid-row: ${props => props.$row};

    max-width: ${props => pxValue(props.$maxw ?? props.$mw)};
    min-width: ${props => pxValue(props.$minw)};
    width: ${props => pxValue(props.$w)};
    min-height: ${props => pxValue(props.$minh)};
    height: ${props => pxValue(props.$h)};
    align-self: ${props => props.$align};
    align-self: ${props => props.$alignSelf};
    justify-self: ${props => props.$justify};
    text-align: ${props => props.$textAlign};
    overflow: ${props => props.$overflow};

    @media (max-width: ${props => pxValue(props.$bp ? props.$bp : 1560)}) {
        width: ${props => pxValue(props.$bpw)};
    }
`;


/**
 * Styled component representing a div
 * Properties:
 *  - All the properties from Layout above
 */
export const Box = styled.div`
    ${Layout};
`;


/**
 * Styled component representing a div with display:flex.
 * Properties:
 *  - All the properties from Layout above +
 *  DEPRECATED - $align is deprecated in favour of $alignItems
 *  - $align (string): align-items
 *  - $alignItems (string): align-items
 *  DEPRECATED - $justify is deprecated in favour of $justifyContent
 *  - $justify (string): justify-content
 *  - $justifyContent (string): justify-content
 *  - $gap (number (in pixels) or string): gap
 *  - $fd (string): flex-direction
 *  - $fw (string): flex-wrap
 */
export const Flex = styled.div`
    ${Layout};
    display: flex;
    ${props => (props.$align || props.$alignItems) && `
        align-items:  ${props.$alignItems ?? props.$align};
    `};
    justify-content:  ${props => props.$justify ?? props.$justifyContent};
    gap: ${props => pxValue(props.$gap)};
    flex-direction: ${props => props.$fd};
    flex-wrap: ${props => props.$fw};
`;


/**
 * Styled component reprensenting a div with display:grid.
 * Properties:
 *  - All the properties from Layout above +
 *  - $columns (string): grid-template-columns
 *  - $rows (string): grid-template-rows
 *  DEPRECATED - $align is deprecated in favour of $alignItems
 *  - $align (string): align-items
 *  - $alignItems (string): align-items
 *  - $justify (string): justify-items
 *  - $gap (number (in pixels) or string): gap
 */
export const Grid = styled.div`
    ${Layout};
    display: grid;
    grid-template-columns: ${props => props.$columns};
    grid-template-rows: ${props => props.$rows};
    gap: ${props => pxValue(props.$gap)};
    align-items:  ${props => props.$align};
    align-items:  ${props => props.$alignItems ?? 'start'};
    justify-items:  ${props => props.$justify};
`;


/**
 * Utility function that takes a props object and separate the "layout" props (that start with $) from above from the rest
 * of the props
 */
export const getLayoutProps = (props) => {
  const layoutProps = Object.keys(props)
    .filter(p => p.startsWith('$'))
    .reduce((acc, p) => ({ ...acc, [p]: props[p] }), {});

  const otherProps = Object.keys(props)
    .filter(p => !p.startsWith('$'))
    .reduce((acc, p) => ({ ...acc, [p]: props[p] }), {});

  return { layoutProps, otherProps };
};


export const pxValue = (value) => {
  if (typeof value === 'number') {
    return `${value}px`;
  } else if (!value) {
    return undefined;
  }
  return value;
};
